import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSchedule } from '../context/ScheduleContext';

const ACUITY_COLOR: Record<string, string> = {
  urgent:  '#dc2626',
  high:    '#d97706',
  routine: '#2563eb',
};

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// Auto-dismiss after 8 seconds.
const AUTO_DISMISS_MS = 8_000;

export function BackfillNotification() {
  const { waitlistHandle } = useSchedule();
  const { notification, dismissNotification, setHighlightedEntryId } = waitlistHandle;

  // Auto-dismiss timer — reset whenever a new notification appears.
  useEffect(() => {
    if (!notification) return;
    const id = setTimeout(dismissNotification, AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [notification, dismissNotification]);

  const handleViewWaitlist = useCallback(() => {
    if (!notification) return;
    const topMatch = notification.match.matches[0];
    if (topMatch) setHighlightedEntryId(topMatch.waitlistEntryId);
    dismissNotification();
  }, [notification, dismissNotification, setHighlightedEntryId]);

  if (!notification) return null;

  const topMatch = notification.match.matches[0];
  const acuityColor = topMatch ? (ACUITY_COLOR[topMatch.acuityLevel] ?? '#2563eb') : '#2563eb';

  const slotTime = (() => {
    try {
      const start = new Date(notification.match.start);
      return start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return '';
    }
  })();

  return createPortal(
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 99998,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        animation: 'fse-notify-slide-in 0.25s ease-out',
        maxWidth: 320,
        minWidth: 260,
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 4px 24px rgba(0,0,0,0.16), 0 1px 4px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
      }}
    >
      {/* Color bar */}
      <div style={{ height: 3, background: '#22c55e' }} />

      {/* Content */}
      <div style={{ padding: '10px 12px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
          {/* Check icon */}
          <div style={{
            flexShrink: 0,
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: '#dcfce7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#16a34a',
            marginTop: 1,
          }}>
            <CheckIcon />
          </div>

          {/* Message */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>
              Backfill found
            </div>
            {topMatch && (
              <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{topMatch.displayLabel}</span>
                {' · '}
                <span style={{ color: acuityColor, fontWeight: 600, textTransform: 'capitalize' }}>
                  {topMatch.acuityLevel}
                </span>
                {slotTime && <span> · {slotTime}</span>}
              </div>
            )}
            {topMatch?.matchReason && topMatch.matchReason.length > 0 && (
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {topMatch.matchReason.slice(0, 2).join(' · ')}
              </div>
            )}
          </div>

          {/* Dismiss */}
          <button
            onClick={dismissNotification}
            aria-label="Dismiss notification"
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              borderRadius: 3,
              marginTop: 2,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#475569'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8'; }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Action */}
        <button
          onClick={handleViewWaitlist}
          style={{
            width: '100%',
            padding: '5px 10px',
            fontSize: 11,
            fontWeight: 600,
            color: '#2563eb',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 4,
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'background 0.1s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#dbeafe'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#eff6ff'; }}
        >
          View waitlist
        </button>
      </div>
    </div>,
    document.body,
  );
}
